"use client";

import type { ExchangeCard, Branch } from "@/lib/flatten-dialog";
import { replacePlaceholders } from "@/lib/flatten-dialog";

interface CheatsheetListProps {
  cards: ExchangeCard[];
  branches: Branch[];
  userDetails: Record<string, string>;
  onCardTap: (cardIndex: number) => void;
}

export function CheatsheetList({ cards, branches, userDetails, onCardTap }: CheatsheetListProps) {
  if (cards.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground p-8">
        No phrases yet
      </div>
    );
  }

  const groups = branches.map((branch, bi) => {
    const nextBranch = branches[bi + 1];
    const endIdx = nextBranch ? nextBranch.startCardIndex : cards.length;
    return {
      branch,
      cards: cards.slice(branch.startCardIndex, endIdx),
      globalStartIndex: branch.startCardIndex,
    };
  });

  return (
    <div className="flex-1 overflow-y-auto">
      {groups.map((group) => (
        <div key={group.branch.index}>
          {branches.length > 1 && (
            <div className="sticky top-0 z-10 px-4 py-2 bg-muted/80 backdrop-blur-sm border-b border-border/30">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {group.branch.label}
              </span>
            </div>
          )}

          {group.cards.map((card, i) => {
            const globalIndex = group.globalStartIndex + i;
            const response = card.response;

            return (
              <button
                key={card.id}
                onClick={() => onCardTap(globalIndex)}
                className="w-full text-left px-4 py-3 border-b border-border/20 hover:bg-muted/50 transition-colors"
              >
                {/* Prompt (them) — compact */}
                <p className="text-[11px] text-muted-foreground/50 truncate mb-0.5">
                  {card.prompt.text}
                </p>

                {/* Response (you) — emphasized */}
                {response ? (
                  <div>
                    <p className="text-sm font-semibold text-foreground truncate">
                      {response.hasPlaceholder
                        ? replacePlaceholders(response.text, userDetails)
                        : response.text}
                    </p>
                    {response.vocalizationTip && (
                      <p className="text-xs text-muted-foreground italic truncate">
                        {response.hasPlaceholder
                          ? replacePlaceholders(response.vocalizationTip, userDetails)
                          : response.vocalizationTip}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground/60 truncate">
                      {response.hasPlaceholder
                        ? replacePlaceholders(response.translation, userDetails)
                        : response.translation}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">End</p>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
